import { PageHeader } from "@bednbody/ui/components/page-header";
import { Callout } from "@bednbody/ui/components/callout";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { Badge } from "@bednbody/ui/components/badge";
import {
  AdminDataTable,
  AdminTableIdentity,
  AdminTableRowMenu,
} from "@bednbody/ui/components/admin-data-table";
import type { ColumnDef } from "@tanstack/react-table";
import { WarningCircleIcon } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { KycReviewDialog } from "./components/KycReviewDialog";
import { buildKycQueue, formatBytes, type KycQueueRow } from "./kyc-queue";
import {
  type KycImage,
  useKycDecision,
  useKycDocumentUrl,
  useKycReviews,
  useKycSubmissions,
} from "./kyc.hooks";

const NO_IMAGES: Record<KycImage, string | null> = { document: null, selfie: null };

/**
 * Client identity review — the screen 0070 was written for.
 *
 * Cash-on-arrival bookings are gated on `profiles.kyc_status = 'verified'`
 * server-side (SECURITY §9a), and this is the only way a client reaches that
 * state. Before this screen existed a superadmin could approve only by calling
 * the Edge route by hand, so no pilot client could actually be verified.
 *
 * WHY SUPERADMIN ONLY. `0003:45` grants hr, admin and superadmin a table-wide
 * UPDATE on `profiles`, which before `0070` meant any of the three could flip a
 * client to verified with no record of who or why. `0070`'s trigger closed that,
 * and the authority was withheld rather than assumed: the grant is a capability,
 * not an approved policy (OQ-81, never-invent-policy.md). Widening it is one
 * role list here plus one in `admin/index.ts`.
 */
export function KycReviewPage() {
  const submissions = useKycSubmissions();
  const reviews = useKycReviews();
  const documentUrl = useKycDocumentUrl();
  const decision = useKycDecision();

  const [active, setActive] = useState<KycQueueRow | null>(null);
  const [openedUrls, setOpenedUrls] = useState<Record<KycImage, string | null>>(NO_IMAGES);
  const [loadingImage, setLoadingImage] = useState<KycImage | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const rows = useMemo(
    () => buildKycQueue(submissions.data ?? [], reviews.data ?? []),
    [submissions.data, reviews.data],
  );

  const columns: ColumnDef<KycQueueRow, unknown>[] = [
    {
      id: "client",
      accessorFn: (row) => row.clientName,
      header: "Client",
      cell: ({ row }) => (
        <AdminTableIdentity
          title={row.original.clientName}
          subtitle={row.original.clientPhone ?? undefined}
          fallback={row.original.clientName.slice(0, 2).toUpperCase()}
        />
      ),
    },
    { id: "document", accessorFn: (row) => row.declaredDocumentType, header: "Declared ID" },
    {
      id: "submitted",
      accessorFn: (row) => row.submittedAt ?? "",
      header: "Submitted",
      cell: ({ row }) =>
        row.original.submittedAt
          ? new Date(row.original.submittedAt).toLocaleString("en-PH", {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "—",
    },
    {
      id: "photo",
      accessorFn: (row) => row.byteSize ?? 0,
      header: "Photo",
      cell: ({ row }) => (row.original.purgedAt ? "Deleted" : formatBytes(row.original.byteSize)),
    },
    {
      id: "state",
      accessorFn: (row) => row.decision ?? "waiting",
      header: "State",
      cell: ({ row }) =>
        row.original.decision === "verified" ? (
          <Badge variant="success">Verified</Badge>
        ) : row.original.decision === "rejected" ? (
          <Badge variant="destructive">Rejected</Badge>
        ) : (
          <Badge variant="info">Waiting</Badge>
        ),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <AdminTableRowMenu
          label={row.original.clientName}
          actions={[
            {
              label: row.original.decision === null ? "Review" : "Re-open",
              onSelect: () => open(row.original),
            },
          ]}
        />
      ),
    },
  ];

  function open(row: KycQueueRow) {
    setActive(row);
    // The previous client's document must never be on screen under a new name.
    setOpenedUrls(NO_IMAGES);
    setErrorMessage(null);
  }

  function close() {
    setActive(null);
    setOpenedUrls(NO_IMAGES);
    setErrorMessage(null);
  }

  async function openImage(image: KycImage) {
    if (!active) return;
    setErrorMessage(null);
    setLoadingImage(image);
    try {
      const result = await documentUrl.mutateAsync({
        clientId: active.clientId,
        submissionId: active.id,
        image,
      });
      setOpenedUrls((current) => ({ ...current, [image]: result.url }));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "We couldn't open that image.");
    } finally {
      setLoadingImage(null);
    }
  }

  async function decide(input: { decision: "verified" | "rejected"; reason: string }) {
    if (!active) return;
    setErrorMessage(null);
    try {
      await decision.mutateAsync({
        clientId: active.clientId,
        submissionId: active.id,
        decision: input.decision,
        // Approvals may carry a note or not; rejections always do (the schema
        // requires it, and a refusal with no recorded why is the one thing the
        // audit trail will be asked about).
        reason: input.reason || undefined,
      });
      toast.success(input.decision === "verified" ? "Client verified." : "Submission rejected.");
      close();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "That decision was refused.");
    }
  }

  if (submissions.isLoading || reviews.isLoading) {
    return <Skeleton className="h-64 motion-reduce:animate-none" />;
  }
  if (submissions.isError || reviews.isError) {
    return (
      <Callout tone="danger" role="alert">
        <p className="font-semibold">We couldn&rsquo;t load the verification queue.</p>
      </Callout>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        description="Clients who sent a photo of their ID to unlock cash on arrival. Approving one lets that client book without paying up front."
        title="Identity verification"
      />

      <div className="flex items-start gap-3 rounded-2xl border border-warning/40 bg-warning/5 p-4">
        <WarningCircleIcon size={20} weight="fill" className="mt-0.5 shrink-0 text-warning" />
        <div className="text-sm">
          <p className="font-semibold">Superadmin decides, because nobody has been assigned yet.</p>
          <p className="mt-0.5 text-muted-foreground">
            Which role reviews identity documents is undecided, so the capability is gated to
            Superadmin and every decision is audited. Opening a document is audited too — the log is
            the record of who has seen a client&rsquo;s ID.
          </p>
        </div>
      </div>

      <AdminDataTable
        presentation="standalone"
        data={rows}
        columns={columns}
        getRowId={(row) => row.id}
        searchText={(row) => [row.clientName, row.declaredDocumentType, row.decision].join(" ")}
        searchPlaceholder="Search client or ID type…"
        facets={[
          {
            id: "state",
            label: "State",
            options: [
              { value: "waiting", label: "Waiting" },
              { value: "verified", label: "Verified" },
              { value: "rejected", label: "Rejected" },
            ],
            getValue: (row) => row.decision ?? "waiting",
          },
        ]}
        initialFacets={{ state: "waiting" }}
        initialSorting={[{ id: "submitted", desc: true }]}
        emptyTitle="Queue is empty"
        emptyDescription="Clients submit from Profile → ID verification in the booking app."
        exportFilename="identity-reviews"
        exportColumns={[
          { header: "Client", value: (row) => row.clientName },
          { header: "Declared ID", value: (row) => row.declaredDocumentType },
          { header: "Submitted", value: (row) => row.submittedAt },
          { header: "State", value: (row) => row.decision ?? "waiting" },
        ]}
      />

      <Callout>
        A decision is written once and cannot be edited — a correction is a new decision on the same
        submission, and the queue shows the newest. Documents are kept until a retention period is
        set; when one is, the image is deleted and this record stays.
      </Callout>

      <KycReviewDialog
        row={active}
        imageUrls={openedUrls}
        loadingImage={loadingImage}
        isDeciding={decision.isPending}
        errorMessage={errorMessage}
        onOpenImage={(image) => void openImage(image)}
        onClose={close}
        onSubmit={(input) => void decide(input)}
      />
    </div>
  );
}
